import React, { useMemo, useState } from 'react';
import {
  startOfMonth, endOfMonth, startOfWeek, endOfWeek, eachDayOfInterval,
  isSameDay, isSameMonth, format, addMonths, subMonths, isToday, parseISO,
} from 'date-fns';
import { ChevronLeft, ChevronRight, X, Clock, Mail, Phone, User, CalendarDays } from 'lucide-react';

const STATUS_STYLE = {
  pending: { chip: 'bg-amber-100 text-amber-800 border-amber-200', dot: 'bg-amber-500', label: 'Pending' },
  accepted: { chip: 'bg-green-100 text-green-800 border-green-200', dot: 'bg-green-500', label: 'Confirmed' },
  reschedule_proposed: { chip: 'bg-indigo-100 text-indigo-800 border-indigo-200', dot: 'bg-indigo-500', label: 'Reschedule sent' },
  rejected: { chip: 'bg-rose-100 text-rose-700 border-rose-200', dot: 'bg-rose-400', label: 'Declined' },
};
const styleFor = (s) => STATUS_STYLE[s] || { chip: 'bg-mahogany/10 text-mahogany border-mahogany/20', dot: 'bg-mahogany/40', label: s || '—' };

const schedDate = (it) => {
  const raw = it.scheduled_start || it.proposed_datetime || it.preferred_datetime;
  if (!raw) return null;
  try { const d = raw.includes('T') ? parseISO(raw) : new Date(raw); return isNaN(d) ? null : d; } catch { return null; }
};
const money = (cur, n) => `${(cur || 'usd').toUpperCase()} ${Number(n || 0).toFixed(2)}`;

const SessionDetail = ({ item, onClose }) => {
  const st = styleFor(item.status);
  const d = schedDate(item);
  return (
    <div className="fixed inset-0 z-[70] bg-black/40 flex items-center justify-center p-4" data-testid="session-detail-modal" onClick={onClose}>
      <div className="bg-cream rounded-3xl p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <div>
            <span className={`inline-block text-xs px-2 py-0.5 rounded-full border ${st.chip}`}>{st.label}</span>
            <h3 className="font-serif-display text-2xl text-burgundy mt-2">{item.session_type_name || 'Session'}</h3>
            {item.session_length ? <p className="text-xs text-mahogany/60">{item.session_length} min</p> : null}
          </div>
          <button onClick={onClose} data-testid="session-detail-close"><X className="w-5 h-5 text-mahogany/70" /></button>
        </div>
        <div className="mt-4 space-y-2 text-sm text-mahogany">
          <p className="flex items-center gap-2"><CalendarDays className="w-4 h-4 text-rose-italic" /> {d ? format(d, 'EEE, d MMM yyyy · h:mm a') : 'Time TBD'}{item.proposed_datetime ? ' (reschedule proposed)' : ''}</p>
          <p className="flex items-center gap-2"><User className="w-4 h-4 text-rose-italic" /> {item.name}</p>
          <p className="flex items-center gap-2"><Mail className="w-4 h-4 text-rose-italic" /> <a href={`mailto:${item.email}`} className="underline">{item.email}</a></p>
          {item.phone && <p className="flex items-center gap-2"><Phone className="w-4 h-4 text-rose-italic" /> <a href={`tel:${item.phone}`} className="underline">{item.phone}</a></p>}
          <p className="flex items-center gap-2"><Clock className="w-4 h-4 text-rose-italic" /> {item.is_free ? 'Volunteer (no charge)' : `Your payout ${money(item.currency, item.payout)}`}</p>
          <p className="text-xs text-mahogany/60">For: {item.teacher_name}{item.purpose === 'meditation_class' ? ' · Meditation class' : ' · Consultation'}</p>
          {item.message && <div className="mt-2 rounded-2xl bg-white border border-rose/30 p-3 text-mahogany/80">{item.message}</div>}
        </div>
      </div>
    </div>
  );
};

const ConsultationCalendar = ({ items = [] }) => {
  const [cursor, setCursor] = useState(new Date());
  const [selected, setSelected] = useState(null);

  const byDay = useMemo(() => {
    const map = {};
    items.forEach((it) => {
      const d = schedDate(it);
      if (!d) return;
      const k = format(d, 'yyyy-MM-dd');
      (map[k] = map[k] || []).push(it);
    });
    Object.values(map).forEach((arr) => arr.sort((a, b) => schedDate(a) - schedDate(b)));
    return map;
  }, [items]);

  const days = useMemo(() => {
    const start = startOfWeek(startOfMonth(cursor), { weekStartsOn: 0 });
    const end = endOfWeek(endOfMonth(cursor), { weekStartsOn: 0 });
    return eachDayOfInterval({ start, end });
  }, [cursor]);

  const agenda = useMemo(() => {
    const withDate = items.map((it) => ({ it, d: schedDate(it) })).filter((x) => x.d);
    withDate.sort((a, b) => a.d - b.d);
    const groups = [];
    let cur = null;
    withDate.forEach(({ it, d }) => {
      const k = format(d, 'yyyy-MM-dd');
      if (!cur || cur.key !== k) { cur = { key: k, date: d, items: [] }; groups.push(cur); }
      cur.items.push(it);
    });
    return groups;
  }, [items]);

  return (
    <div className="space-y-6" data-testid="consultation-calendar">
      <div className="rounded-3xl bg-white/70 border border-rose/40 p-4 sm:p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-serif-display text-xl text-burgundy">{format(cursor, 'MMMM yyyy')}</h3>
          <div className="flex items-center gap-1">
            <button onClick={() => setCursor((c) => subMonths(c, 1))} className="p-2 rounded-full hover:bg-rose-soft/40" data-testid="cal-prev"><ChevronLeft className="w-4 h-4 text-mahogany" /></button>
            <button onClick={() => setCursor(new Date())} className="px-3 py-1.5 rounded-full text-xs border border-rose/50 text-mahogany hover:bg-rose-soft/40" data-testid="cal-today">Today</button>
            <button onClick={() => setCursor((c) => addMonths(c, 1))} className="p-2 rounded-full hover:bg-rose-soft/40" data-testid="cal-next"><ChevronRight className="w-4 h-4 text-mahogany" /></button>
          </div>
        </div>
        <div className="grid grid-cols-7 gap-1 text-center text-[11px] uppercase tracking-widest text-mahogany/50 mb-1">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => <div key={d}>{d}</div>)}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {days.map((day) => {
            const k = format(day, 'yyyy-MM-dd');
            const list = byDay[k] || [];
            const outside = !isSameMonth(day, cursor);
            return (
              <div key={k} className={`min-h-[76px] rounded-xl border p-1.5 text-left ${outside ? 'bg-transparent border-transparent' : 'bg-white/60 border-rose/20'} ${isToday(day) ? 'ring-1 ring-rose' : ''}`} data-testid={`cal-day-${k}`}>
                <div className={`text-xs ${outside ? 'text-mahogany/30' : 'text-mahogany/70'} ${isToday(day) ? 'font-semibold text-burgundy' : ''}`}>{format(day, 'd')}</div>
                <div className="mt-1 space-y-1">
                  {list.slice(0, 3).map((it) => {
                    const st = styleFor(it.status);
                    const d = schedDate(it);
                    return (
                      <button key={it.id} onClick={() => setSelected(it)} className={`w-full text-left text-[10px] leading-tight px-1.5 py-1 rounded-md border truncate ${st.chip} hover:opacity-80`} data-testid={`cal-session-${it.id}`} title={`${it.session_type_name || 'Session'} — ${it.name}`}>
                        {d ? format(d, 'h:mm a') : ''} {it.session_type_name || 'Session'}
                      </button>
                    );
                  })}
                  {list.length > 3 && <div className="text-[10px] text-mahogany/50 px-1">+{list.length - 3} more</div>}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div>
        <h3 className="font-serif-display text-xl text-burgundy mb-2">Agenda</h3>
        {agenda.length === 0 && <p className="text-sm text-mahogany/60" data-testid="agenda-empty">No scheduled sessions yet. New requests will appear here with their preferred time.</p>}
        <div className="space-y-4">
          {agenda.map((g) => (
            <div key={g.key} data-testid={`agenda-day-${g.key}`}>
              <div className="text-xs uppercase tracking-widest text-mahogany/50 mb-1.5">{format(g.date, 'EEEE, d MMMM yyyy')}</div>
              <div className="space-y-1.5">
                {g.items.map((it) => {
                  const st = styleFor(it.status);
                  const d = schedDate(it);
                  return (
                    <button key={it.id} onClick={() => setSelected(it)} className="w-full flex items-center gap-3 rounded-2xl bg-white/70 border border-rose/30 px-3 py-2 text-left hover:bg-rose-soft/30 transition-colors" data-testid={`agenda-item-${it.id}`}>
                      <span className={`w-2 h-2 rounded-full ${st.dot}`} />
                      <span className="text-sm text-mahogany w-20 shrink-0">{d ? format(d, 'h:mm a') : '—'}</span>
                      <span className="text-sm text-burgundy font-medium flex-1 truncate">{it.session_type_name || 'Session'} <span className="text-mahogany/60 font-normal">· {it.name}</span></span>
                      <span className={`text-[11px] px-2 py-0.5 rounded-full border ${st.chip} shrink-0`}>{st.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      {selected && <SessionDetail item={selected} onClose={() => setSelected(null)} />}
    </div>
  );
};

export default ConsultationCalendar;
